import React, { useState, useEffect } from 'react';
import { Container, Card, Button, Table, Badge, Alert, Modal, Form, Spinner } from 'react-bootstrap';
import { 
  ClockIcon,
  DocumentTextIcon,
  PencilSquareIcon,
  DocumentDuplicateIcon,
  ArrowPathIcon
} from '@heroicons/react/24/solid';
import { useNavigate } from 'react-router-dom';
import { orderAPI, recurringAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { Order } from '../../types';
import Header from '../common/Header';
import Footer from '../common/Footer';

const OrderHistory: React.FC = () => {
  const navigate = useNavigate();
  const { user, isExpired } = useAuth();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [duplicatingId, setDuplicatingId] = useState<number | null>(null);
  const [duplicateError, setDuplicateError] = useState('');
  const [showRecurringModal, setShowRecurringModal] = useState(false);
  const [recurringOrderId, setRecurringOrderId] = useState<number | null>(null);
  const [recurringForm, setRecurringForm] = useState({ frequency: 'weekly', next_delivery_date: '', endType: 'never' as 'never' | 'date', end_date: '' });
  const [recurringLoading, setRecurringLoading] = useState(false);
  const [recurringSuccess, setRecurringSuccess] = useState('');

  const leadDays = (user as any)?.min_delivery_lead_days ?? 0;
  const minDeliveryDate = (() => {
    const d = new Date();
    d.setDate(d.getDate() + leadDays);
    return d.toISOString().split('T')[0];
  })();

  const calcNextDeliveryDate = (deliveryDate: string | undefined, frequency: string) => {
    const base = deliveryDate ? new Date(deliveryDate + 'T00:00:00') : new Date();
    if (frequency === 'weekly') base.setDate(base.getDate() + 7);
    else if (frequency === 'biweekly') base.setDate(base.getDate() + 14);
    else if (frequency === 'monthly') base.setMonth(base.getMonth() + 1);
    // Ensure it's not before min delivery date
    const min = new Date(minDeliveryDate + 'T00:00:00');
    const result = base >= min ? base : min;
    return result.toISOString().split('T')[0];
  };

  const handleMakeRecurring = (orderId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setRecurringOrderId(orderId);
    const order = orders.find(o => o.id === orderId);
    const defaultDate = calcNextDeliveryDate(order?.delivery_date, 'weekly');
    setRecurringForm({ frequency: 'weekly', next_delivery_date: defaultDate, endType: 'never', end_date: '' });
    setShowRecurringModal(true);
  };

  const handleCreateRecurring = async () => {
    if (!recurringOrderId) return;
    setRecurringLoading(true);
    try {
      await recurringAPI.createRecurringOrder({
        source_order_id: recurringOrderId,
        frequency: recurringForm.frequency,
        next_delivery_date: recurringForm.next_delivery_date,
        end_date: recurringForm.endType === 'date' ? recurringForm.end_date : null,
      });
      setShowRecurringModal(false);
      setRecurringSuccess('Recurring order created! Manage it from the Recurring Orders page.');
    } catch {
      setDuplicateError('Failed to create recurring order.');
      setShowRecurringModal(false);
    } finally {
      setRecurringLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await orderAPI.getOrders();
      console.log('Orders API response:', response.data);
      // Handle both direct array and paginated response
      const orderData = Array.isArray(response.data) 
        ? response.data 
        : (response.data as any).results || [];
      setOrders(orderData);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setOrders([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'delivered': return 'success';
      case 'processing': return 'warning';
      case 'pending': return 'secondary';
      case 'shipped': return 'info';
      default: return 'secondary';
    }
  };

  const handleDuplicate = async (orderId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setDuplicatingId(orderId);
    try {
      const res = await orderAPI.duplicateOrder(orderId);
      navigate(`/orders/${res.data.id}`);
    } catch {
      setDuplicateError('Failed to duplicate order.');
    } finally {
      setDuplicatingId(null);
    }
  };

  return (
    <div className="d-flex flex-column min-vh-100 app-background">
      <Header />
      <div className="flex-grow-1">
        <Container className="py-3 py-md-5 page-content-padding">
          {/* Page Header */}
          <div className="text-center mb-3 mb-md-5">
            <h1 className="display-4 fw-bold text-primary mb-2 d-flex align-items-center justify-content-center gap-2">
              <ClockIcon className="page-header-icon" style={{ width: '40px', height: '40px' }} />
              Order History
            </h1>
            <p className="lead text-muted mb-0">Track your orders and view past purchases</p>
          </div>

          {duplicateError && (
            <Alert variant="danger" dismissible onClose={() => setDuplicateError('')} className="mb-3">
              {duplicateError}
            </Alert>
          )}

          <Card className="professional-card">
            <Card.Header>
              <h5 className="mb-0 d-flex align-items-center gap-2">
                <DocumentTextIcon style={{ width: '20px', height: '20px' }} />
                Your Orders
              </h5>
            </Card.Header>
          <Card.Body>
            {loading ? (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : orders.length === 0 ? (
              <div className="text-center py-4">
                <h5>No orders found</h5>
                <p className="text-muted">You haven't placed any orders yet.</p>
                <Button variant="primary" onClick={() => navigate('/products')}>
                  Start Shopping
                </Button>
              </div>
            ) : (
              <>
                {/* Desktop Table */}
                <div className="d-none d-md-block">
                  <Table striped hover responsive>
                    <thead>
                      <tr>
                        <th>Order #</th>
                        <th>Order Date</th>
                        <th>Delivery Date</th>
                        <th>Status</th>
                        <th>Items</th>
                        <th>Total</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.isArray(orders) && orders.length > 0 ? (
                        orders.map((order) => (
                          <tr key={order.id}>
                            <td>
                              <strong>{order.order_number || `ORD${order.id.toString().padStart(8, '0')}`}</strong>
                            </td>
                            <td>{(() => { const d = order.created_at || order.order_date; if (!d) return '—'; if (d.includes('T')) return new Date(d).toLocaleDateString(); const [y,m,dd] = d.split('-').map(Number); return `${m}/${dd}/${y}`; })()}</td>
                            <td>
                              {order.delivery_date 
                                ? (() => { const [y,m,d] = order.delivery_date.split('-').map(Number); return `${m}/${d}/${y}`; })()
                                : <span className="text-muted">—</span>}
                            </td>
                            <td>
                              <Badge bg={getStatusVariant(order.status)}>
                                {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                              </Badge>
                            </td>
                            <td>
                              {order.items && order.items.length > 0 ? (
                                <ul className="list-unstyled mb-0">
                                  {order.items.map((item) => (
                                    <li key={item.product.name || item.product.id} className="small">
                                      {item.product.name} x{item.quantity}
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <span className="text-muted">No items</span>
                              )}
                            </td>
                            <td>
                              <strong>${typeof order.total_amount === 'string' ? parseFloat(order.total_amount).toFixed(2) : order.total_amount.toFixed(2)}</strong>
                            </td>
                            <td>
                              <div className="d-flex gap-1 flex-wrap">
                                <Button variant="outline-primary" size="sm" onClick={() => navigate(`/orders/${order.id}`)}>
                                  View
                                </Button>
                                {order.status === 'pending' && (
                                  <Button variant="outline-warning" size="sm" disabled={isExpired} onClick={(e) => { e.stopPropagation(); navigate(`/orders/${order.id}/edit`); }}>
                                    <PencilSquareIcon style={{ width: '14px', height: '14px' }} />
                                  </Button>
                                )}
                                <Button
                                  variant="outline-info"
                                  size="sm"
                                  disabled={duplicatingId === order.id || isExpired}
                                  onClick={(e) => handleDuplicate(order.id, e)}
                                  title="Reorder"
                                >
                                  <DocumentDuplicateIcon style={{ width: '14px', height: '14px' }} />
                                </Button>
                                <Button
                                  variant="outline-success"
                                  size="sm"
                                  disabled={isExpired}
                                  onClick={(e) => handleMakeRecurring(order.id, e)}
                                  title="Make Recurring"
                                >
                                  <ArrowPathIcon style={{ width: '14px', height: '14px' }} />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={7} className="text-center py-4">
                            {loading ? 'Loading orders...' : 'No orders found'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>

                {/* Mobile Card Layout */}
                <div className="d-md-none">
                  {orders.map((order) => (
                    <Card key={order.id} className="mb-3 border" style={{ borderRadius: '12px' }} onClick={() => navigate(`/orders/${order.id}`)}>
                      <Card.Body className="p-3">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div>
                            <strong style={{ fontSize: '0.9rem' }}>{order.order_number || `ORD${order.id.toString().padStart(8, '0')}`}</strong>
                            <div className="text-muted small">
                              {(() => { const d = order.created_at || order.order_date; if (!d) return '—'; if (d.includes('T')) return new Date(d).toLocaleDateString(); const [y,m,dd] = d.split('-').map(Number); return `${m}/${dd}/${y}`; })()}
                            </div>
                          </div>
                          <Badge bg={getStatusVariant(order.status)} className="rounded-pill">
                            {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                          </Badge>
                        </div>
                        {order.items && order.items.length > 0 && (
                          <div className="mb-2">
                            {order.items.slice(0, 2).map((item) => (
                              <small key={item.product.name || item.product.id} className="d-block text-muted">
                                {item.product.name} x{item.quantity}
                              </small>
                            ))}
                            {order.items.length > 2 && (
                              <small className="text-muted">+{order.items.length - 2} more</small>
                            )}
                          </div>
                        )}
                        <div className="d-flex justify-content-between align-items-center">
                          <strong className="text-primary">
                            ${typeof order.total_amount === 'string' ? parseFloat(order.total_amount).toFixed(2) : order.total_amount.toFixed(2)}
                          </strong>
                          <div className="d-flex gap-1">
                            {order.status === 'pending' && (
                              <Button variant="outline-warning" size="sm" disabled={isExpired} onClick={(e) => { e.stopPropagation(); navigate(`/orders/${order.id}/edit`); }}>
                                <PencilSquareIcon style={{ width: '14px', height: '14px' }} />
                              </Button>
                            )}
                            <Button variant="outline-info" size="sm" disabled={duplicatingId === order.id || isExpired}
                              onClick={(e) => handleDuplicate(order.id, e)}>
                              <DocumentDuplicateIcon style={{ width: '14px', height: '14px' }} />
                            </Button>
                            <Button variant="outline-success" size="sm" disabled={isExpired}
                              onClick={(e) => handleMakeRecurring(order.id, e)}>
                              <ArrowPathIcon style={{ width: '14px', height: '14px' }} />
                            </Button>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  ))}
                </div>
              </>
            )}
          </Card.Body>
        </Card>

        <div className="mt-3 d-flex gap-2 flex-wrap">
          <Button variant="primary" onClick={() => navigate('/products')} disabled={isExpired}>
            Place New Order
          </Button>
          <Button variant="outline-success" onClick={() => navigate('/recurring-orders')}>
            <ArrowPathIcon style={{ width: '16px', height: '16px' }} className="me-2" />
            Recurring Orders
          </Button>
        </div>

        {recurringSuccess && (
          <Alert variant="success" className="mt-3" dismissible onClose={() => setRecurringSuccess('')}>
            {recurringSuccess}{' '}
            <Alert.Link onClick={() => navigate('/recurring-orders')}>View Recurring Orders</Alert.Link>
          </Alert>
        )}
        </Container>
      </div>
      <Footer />

      {/* Make Recurring Modal */}
      <Modal show={showRecurringModal} onHide={() => setShowRecurringModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Set Up Recurring Order</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-3">
            This order's items will be automatically reordered on the schedule you choose.
          </p>
          <Form.Group className="mb-3">
            <Form.Label>Frequency</Form.Label>
            <Form.Select
              value={recurringForm.frequency}
              onChange={e => {
                const freq = e.target.value;
                const order = orders.find(o => o.id === recurringOrderId);
                const newDate = calcNextDeliveryDate(order?.delivery_date, freq);
                setRecurringForm(prev => ({ ...prev, frequency: freq, next_delivery_date: newDate }));
              }}
            >
              <option value="weekly">Weekly</option>
              <option value="biweekly">Bi-Weekly</option>
              <option value="monthly">Monthly</option>
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>First Delivery Date</Form.Label>
            <Form.Control
              type="date"
              min={minDeliveryDate}
              value={recurringForm.next_delivery_date}
              onChange={e => setRecurringForm(prev => ({ ...prev, next_delivery_date: e.target.value }))}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Duration</Form.Label>
            <div>
              <Form.Check
                type="radio"
                id="endType-never"
                label="Until I cancel"
                checked={recurringForm.endType === 'never'}
                onChange={() => setRecurringForm(prev => ({ ...prev, endType: 'never', end_date: '' }))}
              />
              <Form.Check
                type="radio"
                id="endType-date"
                label="Until a specific date"
                checked={recurringForm.endType === 'date'}
                onChange={() => setRecurringForm(prev => ({ ...prev, endType: 'date' }))}
              />
            </div>
            {recurringForm.endType === 'date' && (
              <Form.Control
                type="date"
                className="mt-2"
                min={recurringForm.next_delivery_date || minDeliveryDate}
                value={recurringForm.end_date}
                onChange={e => setRecurringForm(prev => ({ ...prev, end_date: e.target.value }))}
              />
            )}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRecurringModal(false)}>Cancel</Button>
          <Button
            variant="success"
            disabled={recurringLoading || !recurringForm.next_delivery_date || (recurringForm.endType === 'date' && !recurringForm.end_date)}
            onClick={handleCreateRecurring}
          >
            {recurringLoading ? <><Spinner size="sm" className="me-2" />Creating...</> : 'Create Recurring Order'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default OrderHistory;